import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Spin, Empty, Button, Segmented } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import forecastService, {
  type ForecastDaily,
  type ForecastHourly,
  type DailyForecast,
  type HourlyForecast,
} from '../../api/forecastService';
import { getEventSource } from '../../utils/eventSource';
import { getParameterValue } from '../../utils/weatherUtils';
import { getWeatherCode, getWeatherCodeForHourly } from '../../utils/weatherUtils';
import { parseWindValue, getFieldIcon } from '../Forecast/fieldRenderers';
import { WindDirectionIcon } from '../Forecast/WindDirectionIcon';
import { useForecastFields } from '../../hooks/useForecastFields';
import type { EventWithCircuit } from '../../api/eventsService';

dayjs.extend(utc);
dayjs.extend(timezone);

interface MobileForecastViewProps {
  event: EventWithCircuit;
  theme: 'light' | 'dark';
}

function useLatestForecasts(event: EventWithCircuit | null) {
  const [dailyForecasts, setDailyForecasts] = useState<ForecastDaily[]>([]);
  const [hourlyForecasts, setHourlyForecasts] = useState<ForecastHourly[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!event) {
      setDailyForecasts([]);
      setHourlyForecasts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const source = getEventSource(event);
      const [dailyResp, hourlyResp] = await Promise.all([
        forecastService.getForecastDaily({
          season_year: event.season_year,
          circuit_id: event.circuit_id,
          start_date: event.start_date,
          source,
        }),
        forecastService.getForecastHourly({
          season_year: event.season_year,
          circuit_id: event.circuit_id,
          start_date: event.start_date,
          source,
        }),
      ]);

      setDailyForecasts(dailyResp.forecasts ?? []);
      setHourlyForecasts(hourlyResp.forecasts ?? []);
    } catch (err) {
      console.error('Error fetching mobile forecasts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load forecasts');
      setDailyForecasts([]);
      setHourlyForecasts([]);
    } finally {
      setLoading(false);
    }
  }, [event]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { dailyForecasts, hourlyForecasts, loading, error, refresh: fetchData };
}

/** Get latest forecast by value_date (most recent issuance) */
function getLatestDaily(forecasts: ForecastDaily[]): ForecastDaily | null {
  if (!forecasts?.length) return null;
  const sorted = [...forecasts].sort(
    (a, b) => new Date(b.value_date).getTime() - new Date(a.value_date).getTime()
  );
  return sorted[0];
}

function getLatestHourly(forecasts: ForecastHourly[]): ForecastHourly | null {
  if (!forecasts?.length) return null;
  const sorted = [...forecasts].sort(
    (a, b) => new Date(b.value_date).getTime() - new Date(a.value_date).getTime()
  );
  return sorted[0];
}

function extractDateVal(forecastDate: string | { value: string } | null): string | null {
  if (!forecastDate) return null;
  if (typeof forecastDate === 'object' && 'value' in forecastDate) return forecastDate.value;
  return String(forecastDate);
}

function extractDatetimeVal(dt: string | { value: string } | null): string | null {
  if (!dt) return null;
  if (typeof dt === 'object' && 'value' in dt) return dt.value;
  return String(dt);
}

export const MobileForecastView: React.FC<MobileForecastViewProps> = ({ event, theme }) => {
  const { dailyForecasts, hourlyForecasts, loading, error, refresh } = useLatestForecasts(event);
  const { dailyCardsFields, hourlyCardsFields, getField, createDefaultField } = useForecastFields();
  const [forecastType, setForecastType] = useState<'daily' | 'hourly'>('daily');

  const isDark = theme === 'dark';
  const tz = event.circuit?.time_zone || 'UTC';

  const latestDaily = useMemo(() => getLatestDaily(dailyForecasts), [dailyForecasts]);
  const latestHourly = useMemo(() => getLatestHourly(hourlyForecasts), [hourlyForecasts]);

  const formatParamValue = useCallback(
    (name: string, value: string | null): string | React.ReactNode => {
      if (value === null || value === undefined) return 'N/A';
      const field = getField(name) ?? createDefaultField(name);

      if (name === 'wind_direction') {
        const dirNum = parseFloat(value);
        if (isNaN(dirNum)) return 'N/A';
        return <WindDirectionIcon direction={dirNum} size={18} showAngle />;
      }
      if (name === 'wind_speed' || name === 'wind_gusts' || name === 'min_wind_speed' || name === 'max_wind_speed' || name === 'avg_wind_speed') {
        const kmh = parseWindValue(value);
        if (kmh === null) return 'N/A';
        return `${kmh.toFixed(1)} km/h`;
      }
      if (field.unit === '%') return `${parseFloat(value).toFixed(0)}%`;
      if (field.unit === '°C' || field.unit === 'C') return `${parseFloat(value).toFixed(1)}°C`;
      if (field.unit === 'hPa') return `${parseFloat(value).toFixed(0)} hPa`;
      return value;
    },
    [getField, createDefaultField]
  );

  if (loading && dailyForecasts.length === 0 && hourlyForecasts.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200, gap: 16 }}>
        <Spin size="large" />
        <span style={{ color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.55)' }}>Loading forecasts…</span>
      </div>
    );
  }

  if (error && dailyForecasts.length === 0 && hourlyForecasts.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <p style={{ color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)', marginBottom: 12 }}>{error}</p>
        <Button icon={<ReloadOutlined />} onClick={refresh}>Retry</Button>
      </div>
    );
  }

  if (!latestDaily && !latestHourly) {
    return (
      <div style={{ padding: 24 }}>
        <Empty description="No forecast data available yet" />
      </div>
    );
  }

  const cardBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
  const textColor = isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)';
  const subColor = isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)';

  const issuedAt = forecastType === 'daily' && latestDaily
    ? dayjs(latestDaily.value_date).tz(tz).format('HH:mm D MMM')
    : latestHourly
    ? dayjs(latestHourly.value_date).tz(tz).format('HH:mm D MMM')
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: 16 }}>
      <Segmented
        value={forecastType}
        onChange={(v) => setForecastType(v as 'daily' | 'hourly')}
        options={[
          { value: 'daily', label: 'Daily' },
          { value: 'hourly', label: 'Hourly' },
        ]}
        block
        size="large"
        style={{ marginBottom: 16 }}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: subColor }}>
          {issuedAt ? `Issued ${issuedAt}` : '—'}
        </span>
        <Button type="text" icon={<ReloadOutlined />} onClick={refresh} size="small">Refresh</Button>
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {forecastType === 'daily' && latestDaily ? (
          <MobileDailyForecast
            forecast={latestDaily}
            tz={tz}
            dailyCardsFields={dailyCardsFields}
            formatParamValue={formatParamValue}
            extractDateVal={extractDateVal}
            cardBg={cardBg}
            textColor={textColor}
            subColor={subColor}
          />
        ) : forecastType === 'hourly' && latestHourly ? (
          <MobileHourlyForecast
            forecast={latestHourly}
            tz={tz}
            hourlyCardsFields={hourlyCardsFields}
            formatParamValue={formatParamValue}
            extractDatetimeVal={extractDatetimeVal}
            cardBg={cardBg}
            textColor={textColor}
            subColor={subColor}
            isDark={isDark}
          />
        ) : (
          <Empty description="No data for this forecast type" />
        )}
      </div>
    </div>
  );
};

interface MobileDailyForecastProps {
  forecast: ForecastDaily;
  tz: string;
  dailyCardsFields: { db_name: string; display_name: string }[];
  formatParamValue: (name: string, value: string | null) => string | React.ReactNode;
  extractDateVal: (d: string | { value: string } | null) => string | null;
  cardBg: string;
  textColor: string;
  subColor: string;
}

function MobileDailyForecast({
  forecast,
  tz,
  dailyCardsFields,
  formatParamValue,
  extractDateVal,
  cardBg,
  textColor,
  subColor,
}: MobileDailyForecastProps) {
  const dayOrder = dailyCardsFields
    .filter((f) => !['weather_code_am', 'weather_code_pm'].includes(f.db_name))
    .sort((a, b) => (a.db_name < b.db_name ? -1 : 1));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {forecast.daily_forecasts?.map((df: DailyForecast) => {
        const dateStr = extractDateVal(
          typeof df.forecast_date === 'object' ? (df.forecast_date as { value: string }) : df.forecast_date
        );
        const weatherCode = getParameterValue(df.parameters, 'weather_code');
        const wc = getWeatherCode(weatherCode.value);
        const dateDisplay = dateStr ? dayjs(dateStr).tz(tz).format('ddd D MMM') : '—';
        const desc = df.weather_description || wc.label;

        return (
          <div
            key={dateStr ?? 'unknown'}
            style={{
              padding: 16,
              backgroundColor: cardBg,
              borderRadius: 10,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <img
                src={wc.icon}
                alt={wc.label}
                style={{ width: 36, height: 36, objectFit: 'contain' }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  const span = document.createElement('span');
                  span.textContent = wc.icon.startsWith('/') ? '☀️' : wc.icon;
                  span.style.fontSize = '24px';
                  (e.target as HTMLImageElement).parentNode?.appendChild(span);
                }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: textColor, fontSize: 16 }}>{dateDisplay}</div>
                <div style={{ fontSize: 13, color: subColor }}>{desc}</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
              {dayOrder.map((field) => {
                const pv = getParameterValue(df.parameters, field.db_name);
                if (pv.value === null) return null;
                return (
                  <span key={field.db_name} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                    {getFieldIcon(field.db_name, { size: 14 })}
                    <span style={{ color: textColor }}>{formatParamValue(field.db_name, pv.value)}</span>
                  </span>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface MobileHourlyForecastProps {
  forecast: ForecastHourly;
  tz: string;
  hourlyCardsFields: { db_name: string; display_name: string }[];
  formatParamValue: (name: string, value: string | null) => string | React.ReactNode;
  extractDatetimeVal: (d: string | { value: string } | null) => string | null;
  cardBg: string;
  textColor: string;
  subColor: string;
  isDark: boolean;
}

function MobileHourlyForecast({
  forecast,
  tz,
  hourlyCardsFields,
  formatParamValue,
  extractDatetimeVal,
  cardBg,
  textColor,
  subColor,
  isDark,
}: MobileHourlyForecastProps) {
  const fieldOrder = hourlyCardsFields
    .filter((f) => !['weather_code_am', 'weather_code_pm'].includes(f.db_name))
    .sort((a, b) => (a.db_name < b.db_name ? -1 : 1));

  // Group hours by calendar date and get per-day metadata
  const daysWithHours = useMemo(() => {
    const hours = forecast.hourly_forecasts ?? [];
    const dayMap = new Map<
      string,
      {
        dateKey: string;
        date: Date;
        previousNight: string | null;
        weatherSummary: string | null;
        hours: HourlyForecast[];
      }
    >();

    hours.forEach((hf) => {
      const dtStr = extractDatetimeVal(
        typeof hf.forecast_datetime === 'object' ? (hf.forecast_datetime as { value: string }) : hf.forecast_datetime
      );
      if (!dtStr) return;
      const dt = dayjs(dtStr).tz(tz);
      const dateKey = dt.format('YYYY-MM-DD');

      if (!dayMap.has(dateKey)) {
        const dayMeta = forecast.daily_metadata?.find((dm) => dm.date === dateKey);
        dayMap.set(dateKey, {
          dateKey,
          date: dt.toDate(),
          previousNight: dayMeta?.previous_night ?? forecast.previous_night ?? null,
          weatherSummary: dayMeta?.weather_summary ?? forecast.weather_summary ?? null,
          hours: [],
        });
      }
      dayMap.get(dateKey)!.hours.push(hf);
    });

    return Array.from(dayMap.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [forecast.hourly_forecasts, forecast.daily_metadata, forecast.previous_night, forecast.weather_summary, extractDatetimeVal, tz]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {daysWithHours.map((dayData) => (
        <div
          key={dayData.dateKey}
          style={{
            padding: 16,
            backgroundColor: cardBg,
            borderRadius: 10,
          }}
        >
          <div style={{ fontWeight: 600, color: textColor, fontSize: 16, marginBottom: 10 }}>
            {dayjs(dayData.dateKey).tz(tz).format('ddd D MMM')}
          </div>

          {(dayData.previousNight || dayData.weatherSummary) && (
            <div style={{ marginBottom: 12 }}>
              {dayData.previousNight && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 12, color: subColor, marginBottom: 4 }}>🌙 Previous Night</div>
                  <div style={{ fontSize: 13, color: textColor, whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                    {dayData.previousNight}
                  </div>
                </div>
              )}
              {dayData.weatherSummary && (
                <div>
                  <div style={{ fontSize: 12, color: subColor, marginBottom: 4 }}>☁️ Weather Conditions</div>
                  <div style={{ fontSize: 13, color: textColor, whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                    {dayData.weatherSummary}
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {dayData.hours.map((hf, idx) => {
              const dtStr = extractDatetimeVal(
                typeof hf.forecast_datetime === 'object'
                  ? (hf.forecast_datetime as { value: string })
                  : hf.forecast_datetime
              );
              const dt = dtStr ? dayjs(dtStr).tz(tz) : null;
              const timeDisplay = dt ? dt.format('HH:mm') : '—';
              const weatherCode = getParameterValue(hf.parameters, 'weather_code');
              const wc = getWeatherCodeForHourly(weatherCode.value, dt?.toDate() ?? new Date(), tz);

              return (
                <div
                  key={dtStr ?? idx}
                  style={{
                    padding: 12,
                    backgroundColor: isDark ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.03)',
                    borderRadius: 8,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                    <img
                      src={wc.icon}
                      alt={wc.label}
                      style={{ width: 36, height: 36, objectFit: 'contain' }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                        const span = document.createElement('span');
                        span.textContent = wc.icon.startsWith('/') ? '☀️' : wc.icon;
                        span.style.fontSize = '24px';
                        (e.target as HTMLImageElement).parentNode?.appendChild(span);
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: textColor, fontSize: 16 }}>{timeDisplay}</div>
                      <div style={{ fontSize: 13, color: subColor }}>{wc.label}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
                    {fieldOrder.map((field) => {
                      const pv = getParameterValue(hf.parameters, field.db_name);
                      if (pv.value === null) return null;
                      return (
                        <span
                          key={field.db_name}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13 }}
                        >
                          {getFieldIcon(field.db_name, { size: 14 })}
                          <span style={{ color: textColor }}>{formatParamValue(field.db_name, pv.value)}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
